import * as React from "react";

import { fetchZonesForLines } from "../api/zones";
import type { Stop, Zone } from "../types";
import { is25xx, isPureNartrafikFromStop } from "../utils/stopType";
import { useZones } from "./useZones";

export type NartrafikZoneGroup = {
  lineId: string;
  name: string;
  zones: Zone[];
};

export function useNartrafikZonesForStop(stop: Stop) {
  const {
    selectedZones,
    activeZoneId,
    addAndActivateZones,
    removeZones,
    setActiveZoneId,
  } = useZones();

  const [zones, setZones] = React.useState<Zone[]>([]);
  const [loading, setLoading] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);

  const isNartrafikStop = isPureNartrafikFromStop(stop);

  const lineKey = isNartrafikStop
    ? Array.from(
        new Set(
          (stop.servedLineIds ?? [])
            .map(String)
            .filter(is25xx)
            .map(Number)
            .filter((lineNumber) => !Number.isNaN(lineNumber))
        )
      )
        .sort((a, b) => a - b)
        .join(",")
    : "";

  React.useEffect(() => {
    if (!lineKey) {
      setZones([]);
      setLoading(false);
      setError(null);
      return;
    }

    let cancelled = false;

    const lineNumbers = lineKey.split(",").map(Number);

    setZones([]);
    setLoading(true);
    setError(null);

    void Promise.all(
      lineNumbers.map((lineNumber) => fetchZonesForLines([lineNumber]))
    )
      .then((zoneGroups) => {
        if (cancelled) return;

        const uniqueZones = Array.from(
          new Map(
            zoneGroups.flat().map((zone) => [String(zone.id), zone] as const)
          ).values()
        );

        setZones(uniqueZones);
      })
      .catch((err) => {
        if (cancelled) return;

        console.error("Kunde inte hämta närtrafikområden för hållplats:", err);

        setZones([]);
        setError("Kunde inte hämta hållplatsens närtrafikområden.");
      })
      .finally(() => {
        if (!cancelled) {
          setLoading(false);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [lineKey]);

  const zoneGroups = React.useMemo<NartrafikZoneGroup[]>(() => {
    const groups = new Map<string, NartrafikZoneGroup>();

    for (const zone of zones) {
      const [lineId] = String(zone.id).split("_");
      const existing = groups.get(lineId);

      if (existing) {
        existing.zones.push(zone);
        continue;
      }

      groups.set(lineId, {
        lineId,
        name: zone.name || `Närtrafikområde ${lineId}`,
        zones: [zone],
      });
    }

    return Array.from(groups.values()).sort((a, b) =>
      a.name.localeCompare(b.name, "sv")
    );
  }, [zones]);

  const selectedZoneIds = React.useMemo(
    () => new Set(selectedZones.map((zone) => String(zone.id))),
    [selectedZones]
  );

  const allZonesSelected =
    zones.length > 0 &&
    zones.every((zone) => selectedZoneIds.has(String(zone.id)));

  const selectedZoneGroupIds = React.useMemo(() => {
    const selectedGroups = new Set<string>();

    for (const group of zoneGroups) {
      const entireGroupSelected =
        group.zones.length > 0 &&
        group.zones.every((zone) => selectedZoneIds.has(String(zone.id)));

      if (entireGroupSelected) {
        selectedGroups.add(group.lineId);
      }
    }

    return selectedGroups;
  }, [zoneGroups, selectedZoneIds]);

  const toggleZoneGroup = React.useCallback(
    (lineId: string) => {
      const group = zoneGroups.find((candidate) => candidate.lineId === lineId);

      if (!group?.zones.length) return;

      const groupZoneIds = group.zones.map((zone) => String(zone.id));

      const entireGroupSelected = groupZoneIds.every((zoneId) =>
        selectedZoneIds.has(zoneId)
      );

      if (entireGroupSelected) {
        removeZones(groupZoneIds);

        if (activeZoneId && groupZoneIds.includes(activeZoneId)) {
          setActiveZoneId(null);
        }

        return;
      }

      addAndActivateZones(group.zones);
      setActiveZoneId(group.zones[0].id);
    },
    [
      zoneGroups,
      selectedZoneIds,
      activeZoneId,
      addAndActivateZones,
      removeZones,
      setActiveZoneId,
    ]
  );

  const toggleZones = React.useCallback(() => {
    if (!zones.length) return;

    const zoneIds = zones.map((zone) => String(zone.id));

    if (allZonesSelected) {
      removeZones(zoneIds);

      if (activeZoneId && zoneIds.includes(activeZoneId)) {
        setActiveZoneId(null);
      }

      return;
    }

    addAndActivateZones(zones);
    setActiveZoneId(zones[0].id);
  }, [
    zones,
    allZonesSelected,
    activeZoneId,
    addAndActivateZones,
    removeZones,
    setActiveZoneId,
  ]);

  return {
    isNartrafikStop,
    zones,
    zoneGroups,
    loading,
    error,
    allZonesSelected,
    selectedZoneGroupIds,
    toggleZoneGroup,
    toggleZones,
  };
}
